import type {LucideIcon} from "lucide-react";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  CreditCard,
  Package,
  PackageCheck,
  Banknote,
  ShoppingBag,
  Truck,
  Wallet,
  XCircle,
  Barcode
} from "lucide-react";
import type {AdminOrderKpis} from "@zaaaz/shared";
import {cn} from "@/lib/utils";

type HeroKpi = {
  key: string;
  label: string;
  value: string | number;
  hint: string;
  icon: LucideIcon;
  accent?: boolean;
  statusFilter?: number;
};

type CompactKpi = {
  key: string;
  label: string;
  value: string | number;
  hint?: string;
  icon: LucideIcon;
  statusFilter?: number;
};

type Props = {
  kpis: AdminOrderKpis;
  activeStatus: number;
  onFilterStatus: (status: number) => void;
};

function formatLei(n: number) {
  return `${Math.round(n).toLocaleString("ro-RO")} lei`;
}

export function OrderKpiCards({kpis, activeStatus, onFilterStatus}: Props) {
  const heroes: HeroKpi[] = [
    {key: "total", label: "All orders", value: kpis.total, hint: "Every status", icon: ShoppingBag, accent: true, statusFilter: 0},
    {key: "processing", label: "Processing", value: kpis.processing, hint: "Awaiting prep", icon: Package, statusFilter: 1},
    {key: "ready", label: "Ready", value: kpis.ready, hint: "Prepared to ship", icon: PackageCheck, statusFilter: 7},
    {key: "shipped", label: "Shipped", value: kpis.shipped, hint: "In transit", icon: Truck, statusFilter: 2},
    {key: "delivered", label: "Delivered", value: kpis.delivered, hint: "Completed", icon: CheckCircle2, statusFilter: 3},
    {key: "sales", label: "Sales volume", value: formatLei(kpis.salesLei), hint: "Non-canceled totals", icon: Banknote, accent: true}
  ];

  const compact: CompactKpi[] = [
    {key: "today", label: "Today", value: kpis.today, hint: kpis.date, icon: CalendarDays},
    {key: "card", label: "Card", value: kpis.card, hint: "Stripe / online", icon: CreditCard},
    {key: "cod", label: "COD", value: kpis.cod, hint: "Cash on delivery", icon: Wallet},
    {key: "cancelled", label: "Cancelled", value: kpis.cancelled, icon: XCircle, statusFilter: 4},
    {key: "payerr", label: "Pay error", value: kpis.paymentError, icon: AlertTriangle, statusFilter: 6},
    {key: "awb", label: "With AWB", value: kpis.withAwb, hint: `Avg ${formatLei(kpis.avgOrderLei)}`, icon: Barcode}
  ];

  return (
    <div className="admin-order-kpis">
      <div className="admin-order-kpis__hero">
        {heroes.map((kpi) => {
          const Icon = kpi.icon;
          const clickable = kpi.statusFilter !== undefined;
          const active = clickable && activeStatus === kpi.statusFilter;
          const className = cn(
            "admin-order-kpi",
            kpi.accent && "admin-order-kpi--accent",
            clickable && "isClickable",
            active && "isActive"
          );
          const body = (
            <>
              <div className="admin-order-kpi__top">
                <span className="admin-order-kpi__label">{kpi.label}</span>
                <span className="admin-order-kpi__icon" aria-hidden="true"><Icon className="h-4 w-4" /></span>
              </div>
              <p className="admin-order-kpi__value">{kpi.value}</p>
              <p className="admin-order-kpi__hint">{kpi.hint}</p>
            </>
          );
          if(clickable) {
            return (
              <button key={kpi.key} type="button" className={className} onClick={() => onFilterStatus(kpi.statusFilter!)}>
                {body}
              </button>
            );
          }
          return <div key={kpi.key} className={className}>{body}</div>;
        })}
      </div>

      <div className="admin-order-kpis__compact">
        {compact.map((kpi) => {
          const Icon = kpi.icon;
          const clickable = kpi.statusFilter !== undefined;
          const active = clickable && activeStatus === kpi.statusFilter;
          const className = cn("admin-order-kpi-sm", clickable && "isClickable", active && "isActive");
          const body = (
            <>
              <span className="admin-order-kpi-sm__icon" aria-hidden="true"><Icon className="h-3.5 w-3.5" /></span>
              <span className="admin-order-kpi-sm__body">
                <span className="admin-order-kpi-sm__label">{kpi.label}</span>
                <span className="admin-order-kpi-sm__value">{kpi.value}</span>
                {kpi.hint ? <span className="admin-order-kpi-sm__hint">{kpi.hint}</span> : null}
              </span>
            </>
          );
          if(clickable) {
            return (
              <button key={kpi.key} type="button" className={className} onClick={() => onFilterStatus(kpi.statusFilter!)}>
                {body}
              </button>
            );
          }
          return <div key={kpi.key} className={className}>{body}</div>;
        })}
      </div>
    </div>
  );
}
